import React, { useCallback, useMemo, useRef, useState } from 'react';
import Tooltip from 'antd/es/tooltip';
import { Checkbox } from 'antd';
import isEmpty from 'lodash/isEmpty';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useSelector, useDispatch } from 'react-redux';
import { IActivity, IActivityDetail } from '@common-types';
import { IAccount, IClientCash, IUserWallet, LocationsTypes, LOGOUT_FIELD_KEY, SHOW_HOTEL_CHAIN_LOGO_OVERLAY_KEY } from '@share/common-types';
import { openActivityDetailPage, getWalletPrices, WalletPriceType, HasPermission, ViewQuotesSharePermission } from '@utils';
import { HotelStars, ExpiredSessionModal, CarouselImages, WalletMessage, WalletRewardMessage } from '@components';
import type { IImage } from '@share/common-types';
import { ActivitySavedProperty } from '@share/components';
import { ActivityCardPriceTemplate } from './price-template';
import { renderNumber, getPreferenceFromAccount, getRankingClass, getToken, isVacationRentalsFromPath, GetDisableNewTabOpen, RootState, AppDispatch } from '@share/utils';
import { activityActions, marginatorActions, activityDetailsActions } from '@share/store/slices';

import { USER_BASE_URL } from '@share/constants';
import { NULL_VALUE, NOT_IMAGE_PROPERTY } from '@constants';
import { NoImageSvg } from '@assets';
import './style.scss';

interface PropsData {
  activity: IActivity;
  matches?: boolean;
  isFromSearch?: boolean;
  isSimilar?: boolean;
  disabled?: boolean;
  compared?: boolean;
  displayCompare?: boolean;
  disableCompare?: boolean;

  showOnMap?: () => void;
  onCompareSelect?: (hotel: IActivity) => void;
}

const zero = 0;
const percents = 100;

export const ActivityCardComponent: React.FC<PropsData> = (props) => {
  const {
    activity,
    disabled = false,
    isSimilar = false,
    compared,
    disableCompare,
    onCompareSelect,
  } = props;

  const cardRef = useRef<HTMLDivElement | null>(null);
  const [isAllSuppliersShown] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);


  const dispatch = useDispatch<AppDispatch>();
  const history = useHistory();

  const menuStore = useSelector((s: RootState) => s.navigationMenuStore);
  const activityStore = useSelector((s: RootState) => s.activityStore);
  const loginStore = useSelector((s: RootState) => s.loginStore);
  const activityLocationsStore = useSelector((s: RootState) => s.activityLocationsStore);
  const marginatorStore = useSelector((s: RootState) => s.marginatorStore);

  const { account, user, userWalletData } = loginStore;
  const { selectedActivitySearchClientCash } = activityStore;
  const { items } = menuStore;

  const getMinPublicPrice = useCallback((): number => {
    const { modalities } = activity;
    const publicPrices = modalities[0].rates[0].publicPrices
    if (publicPrices && publicPrices.length) {
      return Math.min(...publicPrices.map(({ price }) => price));
    }
    return zero;
  }, [activity]);

  const metric = activity.modalities[0].durationMetric;

  const formattedMetric = metric.charAt(0).toUpperCase() + metric.slice(1).toLowerCase();

  const duration = activity.modalities[0].durationValue + ' ' + formattedMetric;
  const rating = activity?.reviews?.averageRating || 0;
  const reviews = activity?.reviews?.total || 0;


  const setCommission = useCallback((value: number) => dispatch(marginatorActions.setCommission(value)), [dispatch]);
  const setHotelDetails = useCallback((hotelInfo: IActivityDetail | null | undefined) => dispatch(activityDetailsActions.setActivityDetails(hotelInfo)), [dispatch]);
  const setSelectedActivity = useCallback((h: IActivity) => dispatch(activityActions.setSelectedActivity(h)), [dispatch]);
  const resetState = () => { }
  const setSelectedActivitySearchClientCash = useCallback((sc: IClientCash) => dispatch(activityActions.setSelectedActivitySearchClientCash(sc)), [dispatch]);

  
  const onHotelPage = useCallback((marginatorCommission: number) => {
    const activitycode = activity.code;
    setCommission(marginatorCommission);
    const { sessionKey } = activityStore;
    const token = getToken(account);
    if (account?.type === (account as any)?.LoginType && !token) {
      setIsSessionExpired(true);
      return;
    }
    setHotelDetails(NULL_VALUE);
    resetState();
    setSelectedActivity(activity);
    const disableNewTabOpen = GetDisableNewTabOpen(account, items);
    void disableNewTabOpen;
    openActivityDetailPage(activitycode, history, sessionKey);
  }, [activity, account, selectedActivitySearchClientCash, history, items, setCommission, setHotelDetails, resetState, setSelectedActivity, setSelectedActivitySearchClientCash, activityStore]);



  const handleRedirect = useCallback(() => {
    const logoutUrlStorage = localStorage.getItem(USER_BASE_URL);
    const logoutPref = getPreferenceFromAccount(account, LOGOUT_FIELD_KEY);
    const logoutUrl = !isEmpty(logoutUrlStorage) ? logoutUrlStorage : logoutPref;
    if (!isEmpty(logoutUrl)) {
      window.location.replace(logoutUrl);
    }
  }, [account]);

  const hasNotRealImageProperty = useCallback((): boolean => {
    return !!(activity.content.mediaImages?.some(img => img.resource?.toLowerCase().includes(NOT_IMAGE_PROPERTY)));
  }, [activity.content.mediaImages]);

  const getTagPositionClass = useCallback((): string => {
    let tagCount = 0;
    const isSoldout = false;
    const pubPrice = getMinPublicPrice();
    const value = marginatorStore.value || 0;
    const firstRate = activity.modalities[0]?.rates[0];
    if (!firstRate) return '';


    const marginatorPrice = firstRate.price / (1 - (value / 100));
    const displayWalletSavings = account?.walletWalletSavings && (getWalletPrices(account as IAccount, userWalletData as IUserWallet, activityStore.selectedActivitySearchClientCash, marginatorPrice, firstRate.maxWalletClientCash, WalletPriceType.Search)?.priceSavings > 0 || getWalletPrices(account as IAccount, userWalletData as IUserWallet, activityStore.selectedActivitySearchClientCash, marginatorPrice, firstRate.maxWalletClientCash, WalletPriceType.Search)?.maxClientCashAmountAllow > 0) && !!getWalletPrices(account as IAccount, userWalletData as IUserWallet, activityStore.selectedActivitySearchClientCash, marginatorPrice, firstRate.maxWalletClientCash, WalletPriceType.Search)?.maxClientCashAmountAllow;

    if (!account?.isSaverRuleProfits && !isSoldout && !disabled) {
      const marginatorsavePercents = Math.floor(percents - (((1 - (firstRate.saving / (pubPrice || 1))) * percents)));
      if (!!isFinite(marginatorsavePercents) && marginatorsavePercents > 0 && !displayWalletSavings && (!isVacationRentalsFromPath(history) || !account?.disableSavingsTagVacationRentals)) {
        tagCount++;
      }
    }

    if (displayWalletSavings) tagCount++;
    if (hasNotRealImageProperty()) tagCount++;

    const classes = ['', 'one-label', 'two-label', 'three-label', 'four-label', 'five-label', 'six-label', 'seven-label'];
    return classes[tagCount] || '';
  }, [activity, account, userWalletData, activityStore, history, disabled, marginatorStore, getMinPublicPrice, hasNotRealImageProperty]);

  const firstRate = activity.modalities[0]?.rates[0];
  const pubPrice = useMemo(() => getMinPublicPrice(), [getMinPublicPrice]);

  const filterdImages: IImage[] = activity.content.mediaImages?.length
    ? activity.content.mediaImages.map((image) => ({
        url: image.resource,
        height: image.height,
        width: image.width,
      }))
    : [];

  const isSoldout = false;
  const value = marginatorStore.value || 0;

  const marginatorPrice = firstRate ? firstRate.price / (1 - (value / 100)) : 0;
  const marginatorSavings = firstRate ? firstRate.saving / (1 - (value / 100)) : 0;
  const marginatorSavingsFinal = account?.breakdownTaxes ? marginatorSavings : pubPrice - marginatorPrice;
  const msave = pubPrice ? (account?.breakdownTaxes ? ((1 - marginatorSavings / pubPrice) * percents) : (marginatorPrice * percents) / pubPrice) : 100;
  const marginatorsavePercents = Math.floor(percents - msave);
  const marginatorCommission = firstRate ? marginatorPrice - firstRate.price : 0;

  const isVacationRentals = isVacationRentalsFromPath(history);
  const hotelPrices = getWalletPrices(account as IAccount, userWalletData as IUserWallet, selectedActivitySearchClientCash, marginatorPrice, firstRate?.maxWalletClientCash || 0, WalletPriceType.Search);
  const displayWalletSavings = account?.walletWalletSavings && (hotelPrices?.priceSavings > 0 || hotelPrices?.maxClientCashAmountAllow > 0) && !!hotelPrices?.maxClientCashAmountAllow;
  const marginatorPriceFinal = displayWalletSavings ? hotelPrices?.priceFinalMax : hotelPrices?.priceFinal;

  const isHotelLocation = activityLocationsStore.location?.realType === LocationsTypes.Hotel || activityLocationsStore.location?.type === LocationsTypes.Hotel;

  const isActivityShareCompareEnabled = account?.activityShareCompareEnabled && 
                                          (!account?.isRSITemplate || ((!account?.activityShareCompareRsiSiteidList?.length && !account?.activityShareCompareRsiOrgidList?.length) || 
                                          (account?.activityShareCompareRsiSiteidList?.includes(items?.siteId?.toString() || '') || account?.activityShareCompareRsiOrgidList?.includes(items?.organizationId?.toString() || ''))));

  return (
    <div className={`activity-card ${disabled ? 'disabled' : ''}`}>
      {disabled && <div className="activity-card__disabled-wrapper" />}
      {isHotelLocation && !isSimilar && <div className="activity-card__hotel-selected-wrapper mt-3"><FormattedMessage id={'activity.card.your_interested'} /></div>}

      <div className={`activity-card-wrapper ${isAllSuppliersShown ? 'prices-open' : ''} ${(isHotelLocation && !isSimilar) ? 'hotel-only' : ''}`} ref={cardRef}>
        <div className="activity-card-wrapper__image-section" onClick={() => onHotelPage(marginatorCommission)}>
          <div className='activity-card-wrapper__container'>
            {!account?.isSaverRuleProfits && !isSoldout && !disabled && !!isFinite(marginatorsavePercents) && marginatorsavePercents > 0 && !displayWalletSavings && (!isVacationRentals || !account?.disableSavingsTagVacationRentals) ? (
              <div className="activity-card-wrapper__label-container">
                <div className={`activity-card-wrapper__label ${getTagPositionClass()}`}>
                  <p className="activity-card-wrapper__label-title">
                    <FormattedMessage id={account?.forceMemberWording ? 'member.save' : 'save'} /> {marginatorsavePercents}%
                  </p>
                </div>
                <div className="activity-card-wrapper__label-corner"></div>
              </div>
            ) : null}

            {displayWalletSavings ? (
              <div className="activity-card-wrapper__label-container">
                <div className={`activity-card-wrapper__label ${getTagPositionClass()}`}>
                  <p className="activity-card-wrapper__label-title">
                    <FormattedMessage id="save.up.to" values={{ number: hotelPrices?.priceSavings ? hotelPrices?.priceSavings : '<1' }} />
                  </p>
                </div>
                <div className="activity-card-wrapper__label-corner"></div>
              </div>
            ) : null}
          </div>
          <ActivitySavedProperty activity={activity} />

          {filterdImages?.length ? (
            <CarouselImages images={filterdImages} chain={(!isEmpty(activity.chain) && (account ? getPreferenceFromAccount(account, SHOW_HOTEL_CHAIN_LOGO_OVERLAY_KEY) === 'true' : false)) ? activity.chain : false} />
          ) : (
            <div className="activity-card-wrapper__no-image">
              <NoImageSvg />
            </div>
          )}
        </div>

        <div className="activity-card-wrapper__details-section">
          <h4 className="activity-card-wrapper__activity-title" onClick={() => onHotelPage(marginatorCommission)}>
            <Tooltip title={activity.name}>{activity.name}</Tooltip>
          </h4>

          <div className="activity-card-wrapper__header-row">
            <div className="activity-card-wrapper__rating-info">
              {reviews !== zero && rating !== zero && (
                <h4 className="activity-card-wrapper__hotel-info-title"><HotelStars stars={1} /></h4>
              )}
              {reviews !== zero && rating !== zero && (
                <div className={`activity-card-wrapper__trust-you-info ${rating === zero ? 'hide-trust-you-info' : ''}`}>
                  <p className="activity-card-wrapper__trust-you-text">
                    <span className={`activity-card-wrapper__bold ${getRankingClass(rating)}`}>{renderNumber(rating, 2)}</span>(
                    {renderNumber(reviews)} <FormattedMessage id="reviews" />)
                  </p>
                </div>
              )}
            </div>
          </div>

          <div className="activity-card-wrapper__features">
            <div className="feature-item">
              <div className="feature-item">
                <span className="clock-icon"><ClockCircleOutlined /></span> {duration}
              </div>
              <div className={`activity-card-wrapper__refundable ${firstRate?.refundable ? 'refundable-ref' : 'refundable-nonref'} ${(!account?.isTravelAgentPro && pubPrice < marginatorPrice) ? 'no-price' : ''}`}>
                {firstRate?.refundable ? (
                  <div className="activity-card-wrapper__refundable-ref"><FormattedMessage id="refundable" /></div>
                ) : (
                  <div className="activity-card-wrapper__refundable-nonref"><FormattedMessage id="non.refundable" /></div>
                )}
              </div>
            </div>
          </div>

          {(!account?.walletWalletSavings && !account?.isMLM) ? (
            <>
              <WalletMessage
                price={marginatorPrice}
                maxClientCash={firstRate?.maxWalletClientCash ?? 0}
                currency={activity.currency}
                type={WalletPriceType.Search}
              />
              <WalletRewardMessage
                price={marginatorPrice}
                maxRewardCash={(firstRate as { maxRewardWalletAmount?: number })?.maxRewardWalletAmount}
                currency={activity.currency}
                type={WalletPriceType.Search}
              />
            </>) : null}
            {isActivityShareCompareEnabled &&(
              <div className="activity-card-wrapper__compare-checkbox" >
                <Checkbox
                  checked={compared}
                  onChange={() => onCompareSelect?.(activity as IActivity)}
                  disabled={disableCompare}
                >
                    {HasPermission(user, ViewQuotesSharePermission) ? <FormattedMessage id="results.activity.card.compare.share" /> : <FormattedMessage id="results.activity.card.compare" />}
                </Checkbox>
              </div>  
            )}
          <ActivityCardPriceTemplate
            currency={activity.currency}
            displayWalletSavings={!!displayWalletSavings}
            marginatorPrice={marginatorPrice}
            marginatorPriceFinal={marginatorPriceFinal}
            marginatorSavings={marginatorSavingsFinal}
            displaySupplierPublicPriceB2C={!!(account?.displaySupplierPublicPriceB2C && account?.isB2C && account?.displayPublicPriceB2CSearch)}
            pubPrice={pubPrice}
            breakdownTaxes={firstRate?.breakdownTaxes}
            maxClientCashAmountAllow={hotelPrices?.maxClientCashAmountAllow}
            walletClientCashName={account?.walletClientCashName}
          />
        </div>
      </div>

      {isSessionExpired && (
        <div className="activity-result-wrapper__expired-message">
          <ExpiredSessionModal visible={isSessionExpired} hasRedirect={true} onHandleRedirect={handleRedirect} />
        </div>
      )}
    </div>
  );
};

export default ActivityCardComponent;
